<div class="w-full max-w-5xl p-8">
    <div class="prose prose-sm !max-w-100 col-span-4 mb-[20px] flex flex-row">
        <button mat-button type="button" (click)="goBack()">
            <mat-icon [svgIcon]="'heroicons_outline:arrow-left'"></mat-icon>
        </button>
        <h2 class="!mt-0 content-center">{{ 'label_interest_registration' | translate }}</h2>
    </div>

    <span class="separator-line block"></span>

    @if (isLoading) {
        <div class="flex justify-center items-center gap-2 pt-8 pb-8">
            <mat-progress-spinner [diameter]="18" [mode]="'indeterminate'"></mat-progress-spinner>
            {{ 'label_fetch_registration' | translate }}
        </div>
    } @else if (!entry) {
        <div class="mt-8 text-secondary">
            {{ errorTranslationKey ?? 'label_registration_not_found' | translate }}
        </div>
    } @else {
        <section class="mt-9 bg-card rounded-2xl shadow p-6">
            <div class="grid gap-4 md:grid-cols-2">
                <div>
                    <div class="text-sm text-secondary">{{ 'label_type' | translate }}</div>
                    <div class="font-medium">{{ entry.typeLabel }}</div>
                </div>

                <div>
                    <div class="text-sm text-secondary">{{ 'label_date' | translate }}</div>
                    <div class="font-medium">{{ formatDate(entry.createdDate) }}</div>
                </div>

                <div>
                    <div class="text-sm text-secondary">{{ 'label_registrant' | translate }}</div>
                    <div class="font-medium">{{ entry.createdByName }}</div>
                </div>

                <div>
                    <div class="text-sm text-secondary">{{ 'label_status' | translate }}</div>
                    <div class="font-medium">
                        @if (entry.isReviewed) {
                            {{ 'label_interest_register_status_reviewed' | translate }}
                        } @else {
                            {{ 'label_interest_register_status_received' | translate }}
                        }
                    </div>
                </div>
            </div>
        </section>

        @if (canReview) {
            <section class="mt-6 bg-card rounded-2xl shadow p-6">
                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h3 class="text-lg font-medium tracking-tight leading-6">
                            {{ 'label_interest_register_review' | translate }}
                        </h3>

                        @if (entry.isReviewed) {
                            <p class="mt-1 text-secondary">
                                {{ 'text_interest_register_reviewed' | translate }}
                            </p>

                            <div class="mt-2 flex flex-col gap-1 text-md text-secondary">
                                @if (entry.reviewedByName) {
                                    <div>
                                        <span>{{ 'label_interest_register_reviewed_by' | translate }}: </span>
                                        <span class="font-medium text-default">
                                            {{ entry.reviewedByName }}
                                        </span>
                                    </div>
                                }

                                @if (entry.reviewedDate) {
                                    <div>
                                        <span>{{ 'label_interest_register_reviewed_date' | translate }}: </span>
                                        <span class="font-medium text-default">
                                            {{ formatDate(entry.reviewedDate) }}
                                        </span>
                                    </div>
                                }
                            </div>
                        } @else {
                            <p class="mt-1 text-secondary">
                                {{ 'text_interest_register_not_reviewed' | translate }}
                            </p>
                        }
                    </div>

                    <div>
                        @if (!entry.isReviewed) {
                            <button
                                mat-flat-button
                                color="primary"
                                type="button"
                                [disabled]="isUpdatingReviewed"
                                (click)="updateReviewed(true)"
                            >
                                @if (isUpdatingReviewed) {
                                    <mat-progress-spinner class="inline-block mr-2" [diameter]="16" [mode]="'indeterminate'">
                                    </mat-progress-spinner>
                                }
                                {{ 'label_interest_register_mark_reviewed' | translate }}
                            </button>
                        } @else {
                            <button mat-stroked-button type="button" [disabled]="isUpdatingReviewed" (click)="updateReviewed(false)">
                                @if (isUpdatingReviewed) {
                                    <mat-progress-spinner class="inline-block mr-2" [diameter]="16" [mode]="'indeterminate'">
                                    </mat-progress-spinner>
                                }
                                {{ 'label_interest_register_mark_unreviewed' | translate }}
                            </button>
                        }
                    </div>
                </div>
            </section>
        }

        @if (detailSections.length > 0) {
            @for (section of detailSections; track section.titleKey) {
                <section class="mt-6 bg-card rounded-2xl shadow p-6">
                    <div class="mb-5">
                        <h3 class="text-lg font-medium tracking-tight leading-6">
                            {{ section.titleKey | translate }}
                        </h3>
                    </div>

                    <dl class="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                        @for (field of section.fields; track field.labelKey) {
                            <div class="min-w-0">
                                <dt class="text-sm font-medium text-secondary">
                                    {{ field.labelKey | translate }}
                                </dt>
                                <dd class="mt-1 text-base font-medium whitespace-pre-line break-words">
                                    {{ field.value }}
                                </dd>
                            </div>
                        }
                    </dl>
                </section>
            }
        } @else if (!isLoading) {
            <section class="mt-6 bg-card rounded-2xl shadow p-6">
                <div class="text-secondary">
                    {{ 'text_interest_register_no_detail_data' | translate }}
                </div>
            </section>
        }

        @if (entry.attachments.length > 0) {
            <section class="mt-6 bg-card rounded-2xl shadow p-6">
                <h3 class="text-lg font-medium mb-4">{{ 'label_attachment' | translate }}</h3>

                <div class="divide-y">
                    @for (attachment of entry.attachments; track attachment.id) {
                        <div class="flex items-center justify-between py-3">
                            <div class="min-w-0">
                                <div class="font-medium truncate">{{ attachment.fileName }}</div>
                                <div class="text-sm text-secondary">{{ formatFileSize(attachment.fileSize) }}</div>
                            </div>

                            <button mat-stroked-button type="button" class="c-link" (click)="downloadAttachment(attachment)">
                                <mat-icon [svgIcon]="'heroicons_outline:arrow-down-tray'"></mat-icon>
                                {{ 'label_open' | translate }}
                            </button>
                        </div>
                    }
                </div>
            </section>
        }
    }
</div>
